import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { SafeAreaView } from "react-native-safe-area-context";
import * as Sentry from "@sentry/react-native";
import { useFocusEffect, useRouter } from "expo-router";

import { AppUserSession } from "@/schemas/auth/session";
import ExploreByCategoryScreenHeader from "@/screens/Explore/ExploreCategory/ExploreByCategoryScreenHeader";
import {
  RecommendationCategoryFeed,
  UserRecommendedItem,
} from "@/schemas/user/explore";
import { Spacings } from "@/styling/spacings";
import { EXPLORE_CONSTANTS, calculateCardDimensions } from "@/utils/explore";
import { themes } from "@/Components/Styling/themeConfigs";
import EmptyContentView from "@/Components/EmptyState/EmptyContentView";
import RecommendationService from "@/services/user/recommendation/api";
import UnlockUsersModal from "@/screens/Explore/Components/UnlockFlow/UnlockUsersModal";
import { useSession } from "@/context/auth/useSession";
import { UserProfile } from "@/schemas/user/profile";
import ProfileService from "@/services/user/profile";

type Props = {
  session: AppUserSession;
  categoryData: RecommendationCategoryFeed;
  numOfColumns: number;
  renderProfileCard: (params: {
    item: UserRecommendedItem;
    itemWidth: number;
    itemHeight: number;
    onLockedProfileClick: () => void;
  }) => React.ReactElement;
  isDevTab?: boolean;
  isMarketingMode?: boolean;
  registerRefetch?: (refetch: () => Promise<void>) => void;
};

const currentTheme = themes.morning;
const PAGINATION_LIMIT = 12;

function ExploreCategoryScreenBase({
  session,
  numOfColumns,
  categoryData,
  renderProfileCard,
  isDevTab = false,
  isMarketingMode = false,
  registerRefetch,
}: Props): React.JSX.Element {
  const router = useRouter();

  const [categoryDataState, setCategoryDataState] =
    useState<RecommendationCategoryFeed>(categoryData);

  // Dimensions
  const { width } = useWindowDimensions();
  const { width: itemWidth, height: itemHeight } = useMemo(
    () =>
      calculateCardDimensions(
        width, // screen width
        Spacings.s1 * (numOfColumns + 1), // spacing between cards + screen edges
        numOfColumns, // numColumns
        EXPLORE_CONSTANTS.ASPECT_RATIO,
        1, // scaleFactor
      ),
    [width, numOfColumns],
  );

  // User Profile
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const profileService = useMemo(
    () => new ProfileService(session),
    [session.token], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fetchProfiles = useCallback(async (): Promise<void> => {
    try {
      const profile = await profileService.getUserProfile(
        session.identity.username,
      );
      setUserProfile(profile);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    }
  }, [profileService, session.identity.username]);

  // initial fetch on focus
  useFocusEffect(
    useCallback(() => {
      fetchProfiles();
    }, [fetchProfiles]),
  );

  // Unlocking
  const { refreshUserIdentity } = useSession();
  const [isUnlockModalVisible, setIsUnlockModalVisible] = useState(false);
  const [isRefreshingUnlocking, setIsRefreshingUnlocking] = useState(false);
  // close unlock modal if unlocking stage changes
  const prevUnlockLevelRef = useRef(session.identity.unlockingStage);
  useEffect(() => {
    if (prevUnlockLevelRef.current !== session.identity.unlockingStage) {
      setIsUnlockModalVisible(false);
      prevUnlockLevelRef.current = session.identity.unlockingStage;
    }
  }, [session.identity.unlockingStage]);

  const handleRefreshUnlocking = async (): Promise<void> => {
    setIsRefreshingUnlocking(true);
    try {
      await Promise.all([
        refreshUserIdentity(), // to get updated unlocking stage
        refetchRecommendations(), // to get updated recommendation profile items with unlocked status
        fetchProfiles(), // to get updated user profile for modal display and score calculation
      ]);
    } finally {
      setIsRefreshingUnlocking(false);
    }
  };

  // Recommendation Data
  const [isRefreshingRecommendations, setIsRefreshingRecommendations] =
    useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isFetchingRef = useRef(false);
  const recsService = useMemo(
    () => new RecommendationService(session),
    [session.token], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fetchRecommendations = useCallback(
    async (mode: "refresh" | "loadMore"): Promise<void> => {
      // Prevent any operation if either refreshing or loading more is running
      if (isFetchingRef.current) return;
      if (mode === "loadMore" && categoryDataState.items.next === null) {
        return;
      }

      // Set logic gate and appropriate UI loading state
      isFetchingRef.current = true;

      if (mode === "refresh") {
        setIsRefreshingRecommendations(true);
      } else if (mode === "loadMore") {
        setIsLoadingMore(true);
      }

      const request = {
        // filter: userFilters,
        jobId: categoryDataState.jobId,
        category: categoryDataState.category,
        page: {
          limit: PAGINATION_LIMIT,
          next: mode === "refresh" ? null : categoryDataState.items.next,
        },
      };

      try {
        const response = await recsService.getCategoryRecommendations(
          request,
          isDevTab === true || isMarketingMode === true,
        );
        setCategoryDataState((prev) => ({
          ...prev,
          items: {
            ...prev.items,
            items:
              mode === "refresh"
                ? response.items.items
                : [...prev.items.items, ...response.items.items],
            next: response.items.next,
            total: response.items.total,
          },
        }));
      } catch (error) {
        Sentry.captureException(error);
        Alert.alert("Something went wrong", "Please try again later.", [
          { text: "OK" },
        ]);
      } finally {
        isFetchingRef.current = false;
        setIsLoadingMore(false);
        setIsRefreshingRecommendations(false);
      }
    },
    [recsService, categoryDataState, isDevTab],
  );

  const fetchMoreRecommendations = useCallback(
    (): Promise<void> => fetchRecommendations("loadMore"),
    [fetchRecommendations],
  );

  const refetchRecommendations = useCallback(
    (): Promise<void> => fetchRecommendations("refresh"),
    [fetchRecommendations],
  );

  // Provide refetch function to parent via registerRefetch callback
  useEffect(() => {
    registerRefetch?.(refetchRecommendations);
    return () => {
      registerRefetch?.(async () => {}); // Clear the reference on unmount
    };
  }, [registerRefetch, refetchRecommendations]);

  const renderItem = useCallback(
    ({
      item,
    }: {
      index: number;
      item: UserRecommendedItem;
    }): React.ReactElement => {
      return renderProfileCard({
        item,
        itemWidth,
        itemHeight,
        onLockedProfileClick: () => {
          setIsUnlockModalVisible(true);
        },
      });
    },
    [renderProfileCard, itemWidth, itemHeight],
  );

  return (
    <LinearGradient
      colors={currentTheme.bg}
      style={styles.gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
    >
      <SafeAreaView style={styles.safeArea} edges={["top"]}>
        <View style={styles.container}>
          <ExploreByCategoryScreenHeader
            category={categoryDataState.category}
            key={categoryDataState.category}
            profileCount={categoryDataState.items.total}
          />

          <>
            <FlatList
              data={categoryDataState.items.items}
              renderItem={renderItem}
              onRefresh={refetchRecommendations}
              refreshing={isRefreshingRecommendations}
              onEndReachedThreshold={0.5}
              onEndReached={fetchMoreRecommendations}
              showsVerticalScrollIndicator={false}
              numColumns={numOfColumns}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.flatListContainer}
              columnWrapperStyle={
                numOfColumns > 1 ? { gap: Spacings.s1 } : undefined
              }
              ItemSeparatorComponent={() => (
                <View style={{ height: Spacings.s1 }} />
              )}
              ListFooterComponent={
                isLoadingMore ? (
                  <ActivityIndicator
                    size="small"
                    style={styles.loadMoreActivityIndicator}
                  />
                ) : null
              }
              ListEmptyComponent={
                <EmptyContentView
                  emptyTitle={"Something went wrong. Please try again."}
                  primaryMaterialCommunityIconName="exclamation-thick"
                  actionButtonText="Go Back"
                  onActionButtonPress={() => router.back()}
                />
              }
            />
            {userProfile && (
              <UnlockUsersModal
                session={session}
                userProfile={userProfile}
                isModalVisible={isUnlockModalVisible}
                onRequestClose={() => setIsUnlockModalVisible(false)}
                onRequestRefresh={handleRefreshUnlocking}
                isRefreshing={isRefreshingUnlocking}
              />
            )}
          </>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  gradient: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
    backgroundColor: "transparent",
  },
  container: {
    flex: 1,
    marginHorizontal: Spacings.s1,
  },
  flatListContainer: {
    flexGrow: 1,
    paddingBottom: Spacings.s1,
  },
  loadMoreActivityIndicator: {
    marginVertical: 15,
  },
});

export default ExploreCategoryScreenBase;
